/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiBadge,
  EuiBetaBadge,
  EuiButtonIcon,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiPopover,
  EuiText,
  EuiTextColor,
  useGeneratedHtmlId,
} from '@elastic/eui'

import DestructiveActionRegularConfirmationModal from '@modules/cui/ConfirmationModal/DestructiveActionRegularConfirmationModal'
import { FormattedMessage } from '@modules/i18n'
import { useToggle } from '@modules/utils/hooks/useToggle'

import { ZoneDisplayNameFlexItem } from '../cluster-page/ZoneDisplayNameFlexItem'

import { SkeletonServiceItem } from './SkeletonServiceItem'

import type { ComponentProps, FC, ReactNode } from 'react'
import type { ServiceCardStatusBadge } from './types'

type Props = {
  serviceName: string
  badge: ServiceCardStatusBadge
  zoneId?: string | undefined
  description: ReactNode
  rightSideContent?: ReactNode
  isSubdued?: boolean
  isSkeleton?: boolean
  onDisable?: () => void
  additionalMenuItems?: Array<ComponentProps<typeof EuiContextMenuItem> & { key: string }>
  showContextMenu?: boolean
}

const messages = defineMessages({
  openMenu: {
    id: 'ccm.cluster-services.open-menu',
    defaultMessage: 'Open service context menu',
  },
})

const ServiceItem: FC<Props> = ({
  serviceName,
  badge,
  zoneId,
  description,
  rightSideContent,
  isSubdued,
  isSkeleton,
  additionalMenuItems,
  onDisable,
  showContextMenu = true,
}) => {
  const { formatMessage } = useIntl()

  const generatedId = useGeneratedHtmlId({
    prefix: `disable-${serviceName}`,
  })

  const [isDisconnectionModalOpen, toggleDisconnectionModal] = useToggle()

  const [isPopoverOpen, togglePopover] = useToggle()

  const cleanUpPopoverAndModal = () => {
    toggleDisconnectionModal()
    togglePopover()
  }

  const onDisableButtonConfirm = () => {
    onDisable?.()
    cleanUpPopoverAndModal()
  }

  return (
    <Fragment>
      {isDisconnectionModalOpen && (
        <DestructiveActionRegularConfirmationModal
          modalTitle={
            <FormattedMessage
              id='ccm.cluster-services.disable-modal.title'
              defaultMessage='Disable service'
              values={{ serviceName }}
            />
          }
          cancelButtonLabel={
            <FormattedMessage
              id='ccm.cluster-services.disable-modal.modal-cancel-button'
              defaultMessage='Cancel'
            />
          }
          deleteButtonLabel={
            <FormattedMessage
              id='ccm.cluster-services.disable-modal.delete-button-label'
              defaultMessage='Disable service'
            />
          }
          onDeleteButtonConfirm={onDisableButtonConfirm}
          onCloseConfirmModal={cleanUpPopoverAndModal}
          content={
            <FormattedMessage
              id='ccm.cluster-services.disable-modal.regular-message'
              defaultMessage='Disabling this service will permanently remove all related setup and configuration from your cluster. You can re-enable it later, but your progress will be lost.'
              values={{
                serviceName: <strong>{serviceName}</strong>,
              }}
            />
          }
        />
      )}
      <EuiPanel
        hasShadow={false}
        hasBorder={true}
        paddingSize='l'
        color={isSubdued ? 'subdued' : undefined}
      >
        {isSkeleton ? (
          <SkeletonServiceItem />
        ) : (
          <EuiFlexGroup justifyContent='spaceBetween'>
            <EuiFlexItem>
              <EuiFlexGroup direction='column' gutterSize='s'>
                <EuiFlexItem>
                  <EuiFlexGroup gutterSize='s' alignItems='center'>
                    <EuiFlexItem grow={false}>
                      <EuiText size='m'>
                        <strong>{serviceName}</strong>
                      </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <ServiceCardStatusBadgeSelector badge={badge} />
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
                <ZoneDisplayNameFlexItem zoneId={zoneId} />
                <EuiFlexItem>
                  <EuiText color='subdued' size='s'>
                    {description}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup direction='column'>
                <EuiFlexItem grow={false}>
                  <EuiFlexGroup gutterSize='s' alignItems='center'>
                    <EuiFlexItem>{rightSideContent}</EuiFlexItem>
                    {showContextMenu && (
                      <EuiFlexItem>
                        <EuiPopover
                          button={
                            <EuiButtonIcon
                              size='xs'
                              iconType='boxesVertical'
                              aria-label={formatMessage(messages.openMenu)}
                              onClick={togglePopover}
                            />
                          }
                          isOpen={isPopoverOpen}
                          closePopover={togglePopover}
                          anchorPosition='downCenter'
                          panelPaddingSize='none'
                        >
                          <EuiContextMenuPanel
                            items={[
                              <EuiContextMenuItem
                                key={generatedId}
                                onClick={() => {
                                  toggleDisconnectionModal()
                                }}
                              >
                                <EuiTextColor color='danger'>
                                  <FormattedMessage
                                    id='ccm.cluster-services.disable'
                                    defaultMessage='Disable {serviceName}'
                                    values={{ serviceName }}
                                  />
                                </EuiTextColor>
                              </EuiContextMenuItem>,
                              ...(additionalMenuItems ?? []).map((item) => (
                                <EuiContextMenuItem key={item.key} onClick={item.onClick}>
                                  {item.children}
                                </EuiContextMenuItem>
                              )),
                            ]}
                          />
                        </EuiPopover>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}
      </EuiPanel>
    </Fragment>
  )
}

const ServiceCardStatusBadgeSelector: FC<{ badge: ServiceCardStatusBadge }> = ({ badge }) => {
  switch (badge) {
    case 'enabled':
      return (
        <EuiBadge color='success'>
          <FormattedMessage id='ccm.cluster-services.badge.enabled' defaultMessage='Enabled' />
        </EuiBadge>
      )

    case 'not_enabled':
      return (
        <EuiBadge color='subdued'>
          <FormattedMessage
            id='ccm.cluster-services.badge.not-enabled'
            defaultMessage='Not enabled'
          />
        </EuiBadge>
      )

    case 'coming_soon':
      return (
        <EuiBetaBadge
          label={
            <FormattedMessage
              id='ccm.cluster-services.badge.coming-soon'
              defaultMessage='Coming soon'
            />
          }
          size='s'
          tooltipContent={undefined}
        />
      )

    case 'trial_expired':
      return (
        <EuiBadge iconType='warning' color='warning'>
          <FormattedMessage
            id='ccm.cluster-services.badge.trial-expired'
            defaultMessage='Cloud trial expired'
          />
        </EuiBadge>
      )
    default:
      return null
  }
}

export default ServiceItem
